import { Content, ContentNode, ContentType } from '@amzn/awscat-pattern-library-service-model';

import { InlineTextFilter } from './InlineTextOperations';
import { SpecialSectionId } from './SectionOperations';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasStringId(value: unknown): value is Record<string, unknown> & { id: string } {
  return isRecord(value) && typeof value.id === 'string' && value.id.length > 0;
}

function structuredSectionItems(content: Content, sectionId: SpecialSectionId): readonly unknown[] {
  const section = content.contentData.contentSections.find((item) => item.id === sectionId);
  return Array.isArray(section?.contentData) ? section.contentData : [];
}

function sectionFilter(node: ContentNode): InlineTextFilter {
  return { type: 'section', sectionId: node.id, nodeType: node.nodeType };
}

function listEpicFilters(content: Content): readonly InlineTextFilter[] {
  const filters: InlineTextFilter[] = [];

  for (const epic of structuredSectionItems(content, SpecialSectionId.Epics)) {
    if (!hasStringId(epic)) continue;

    filters.push(
      { type: 'epic_title', epicId: epic.id },
      { type: 'epic_description', epicId: epic.id },
    );

    if (!Array.isArray(epic.tasks)) continue;
    for (const task of epic.tasks) {
      if (!hasStringId(task)) continue;

      filters.push(
        { type: 'epic_task_title', taskId: task.id },
        { type: 'epic_task_description', taskId: task.id },
      );
    }
  }

  return filters;
}

function listTroubleshootingFilters(content: Content): readonly InlineTextFilter[] {
  const filters: InlineTextFilter[] = [];

  for (const item of structuredSectionItems(content, SpecialSectionId.Troubleshooting)) {
    if (!hasStringId(item)) continue;

    filters.push(
      { type: 'troubleshooting_issue', troubleshootingId: item.id },
      { type: 'troubleshooting_solution', troubleshootingId: item.id },
    );
  }

  return filters;
}

/** Returns filters for the page body and every section rendered on that page. */
export function listPageInlineTextFilters(page: ContentNode): readonly InlineTextFilter[] {
  return [sectionFilter(page), ...(page.contentSections ?? []).map(sectionFilter)];
}

/** Returns all inline-text filters represented by one rendered section. */
export function listSectionInlineTextFilters(
  content: Content,
  node: ContentNode,
): readonly InlineTextFilter[] {
  const contentType = content.contentMetadata.contentType;

  if (contentType === ContentType.Pattern && node.id === SpecialSectionId.Epics) {
    return listEpicFilters(content);
  }

  if (contentType === ContentType.Pattern && node.id === SpecialSectionId.Troubleshooting) {
    return listTroubleshootingFilters(content);
  }

  return [sectionFilter(node)];
}

/** Returns all finding filters represented by one content navigator item. */
export function listNavigatorInlineTextFilters(
  content: Content,
  node: ContentNode,
): readonly InlineTextFilter[] {
  const contentType = content.contentMetadata.contentType;
  if (contentType === ContentType.Guide || contentType === ContentType.Strategy) {
    return listPageInlineTextFilters(node);
  }

  return listSectionInlineTextFilters(content, node);
}
